/**
 * Helpers for deciding whether persisted floor graphJson should be treated as
 * "having local content" (do not overwrite from API) vs empty (safe to merge from server).
 */

/** Minimal API map_data floor shape used by editor sync and restoration. */
export interface ApiMapFloorLike {
  name: string
  order?: number
  graphJson?: unknown
}

export interface ApiMapDataLike {
  floors?: ApiMapFloorLike[]
}

export interface StoreFloorLike {
  name: string
  order: number
}

/**
 * checkHasLocalData returns true when graphJson should be preserved and not replaced by server data.
 * Handles both object and string graphJson formats (API often returns JSON string).
 */
export function checkHasLocalData(graphJson: unknown): boolean {
  if (!graphJson) return false

  try {
    const parsed =
      typeof graphJson === 'string' ? JSON.parse(graphJson as string) : graphJson

    if (!parsed || typeof parsed !== 'object') return false

    if (Array.isArray((parsed as { cells?: unknown }).cells) && (parsed as { cells: unknown[] }).cells.length > 0) {
      return true
    }

    const keys = Object.keys(parsed as object)
    const meaningfulKeys = keys.filter((k) => k !== 'cells')
    if (meaningfulKeys.length > 0) {
      return meaningfulKeys.some((key) => {
        const value = (parsed as Record<string, unknown>)[key]
        if (Array.isArray(value)) return value.length > 0
        if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0
        return Boolean(value)
      })
    }

    return false
  } catch {
    // If parsing fails, assume there's local data to be safe (avoid wiping user data)
    return true
  }
}

/**
 * findMatchingApiFloor matches a store floor to an API floor by name and order
 * (same rule as useFloorSelection).
 */
export function findMatchingApiFloor(
  mapData: ApiMapDataLike | null | undefined,
  storeFloor: StoreFloorLike
): ApiMapFloorLike | undefined {
  if (!mapData?.floors?.length) return undefined
  // Match useFloorSelection: order falls back to forEach index (same as apiFloor.order || index there)
  return mapData.floors.find(
    (apiFloor, index) =>
      storeFloor.name === apiFloor.name && storeFloor.order === (apiFloor.order || index)
  )
}
